import { useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import {
  orientationFieldConfiguration,
  orientationInitialValues,
} from "../../../formConfigs/Recruiting/orientationFieldConfig";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const fetchAvailableTrucks = async (token: string | null | undefined) => {
  const { data } = await axios.get(
    `${apiVariable}/recruiting/orientations/available-trucks`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const fetchAvailableTrailers = async (token: string | null | undefined) => {
  const { data } = await axios.get(
    `${apiVariable}/recruiting/orientations/available-trailers`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const getUnitNumber = (fieldValue: any) => {
  if (
    fieldValue &&
    typeof fieldValue === "object" &&
    "unitNumber" in fieldValue
  ) {
    return fieldValue.unitNumber;
  }
  return undefined;
};

const OrientationSchedulePopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: trucks = [] } = useQuery({
    queryKey: ["availableTrucks"],
    queryFn: () => fetchAvailableTrucks(token),
    enabled: open,
  });

  const { data: trailers = [] } = useQuery({
    queryKey: ["availableTrailers"],
    queryFn: () => fetchAvailableTrailers(token),
    enabled: open,
  });

  const autocompleteData = useMemo(
    () => ({
      availableTrucks: (trucks as any[]).map((truck) => ({
        id: truck.id?.toString?.() ?? truck.id,
        label: truck.unitNumber ?? truck.unit ?? truck.label ?? "Unknown",
        unitNumber: truck.unitNumber ?? truck.unit ?? "",
      })),
      availableTrailers: (trailers as any[]).map((trailer) => ({
        id: trailer.id?.toString?.() ?? trailer.id,
        label: trailer.unitNumber ?? trailer.unit ?? trailer.label ?? "Unknown",
        unitNumber: trailer.unitNumber ?? trailer.unit ?? "",
      })),
    }),
    [trucks, trailers]
  );

  const orientationMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/recruiting/orientation`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["orientations"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit orientation");
    },
  });

  const submitHandler = (data: any) => {
    const truckId = getId(data.truckId);
    const trailerId = getId(data.trailerId);
    orientationMutation.mutate({
      ...data,
      truckId,
      trailerId,
      truckUnitNumber: getUnitNumber(data.truckId),
      trailerUnitNumber: getUnitNumber(data.trailerId),
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Create Orientation
      </Button>
      <GenericFormPopup
        formFields={orientationFieldConfiguration}
        initialValues={orientationInitialValues}
        title="Orientation Schedule"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={autocompleteData}
        loading={orientationMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Orientation created successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default OrientationSchedulePopup;
